import { runPositionShift } from "@/lib/positionShift";
import * as Portal from "@radix-ui/react-portal";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import TooltipBox from "../TooltipBox";

const TOOLTIP_WIDTH = 200;
const MARGIN = { top: 10, right: 10 + TOOLTIP_WIDTH, bottom: 30, left: 90 };
// Top inset so a callout stack pushed to the top doesn't sit flush against the chart edge.
const TOOLTIP_STACK_TOP_PADDING = 16;

export type TooltipData = {
  xPos: number;
  yPos: number;
  component: React.ReactNode;
};

type TooltipProps = {
  tooltipData: TooltipData | null;
  children: React.ReactNode;
};

export function Tooltip({ tooltipData, children }: TooltipProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const PADDING = 16;

  useEffect(() => {
    const width = divRef.current ? divRef.current.offsetWidth : 0;
    setWidth(width);
  });

  useEffect(() => {
    const height = divRef.current ? divRef.current.offsetHeight : 0;
    setHeight(height);
  });

  if (tooltipData === null) return null;

  let { xPos, yPos } = tooltipData;
  if (tooltipData.xPos + width + PADDING > window.innerWidth) {
    xPos = tooltipData.xPos - width - PADDING;
  } else {
    xPos = xPos + PADDING;
  }

  if (tooltipData.yPos + height + PADDING > window.innerHeight) {
    yPos = tooltipData.yPos - height - PADDING;
    yPos = yPos + PADDING;
  }

  return (
    <AnimatePresence>
      <motion.div
        key="tooltip"
        initial={{ top: yPos, left: xPos, opacity: 0 }}
        animate={{ top: yPos, left: xPos, opacity: 1 }}
        className="absolute z-50"
        ref={divRef}
      >
        <div className="min-w-64 rounded border bg-background border-border bg-background p-1 shadow-xl">
          {children}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

type NonOverlappingTooltipPosition = {
  boxPosition: { x: number; y: number };
  position: { x: number; y: number };
  width: number;
  height: number;
  compId: string;
};

export function deriveNonOverlappingTooltipPositions(
  comps: {
    position: {
      x: number;
      y: number;
    };
    width: number;
    height: number;
    compId: string;
  }[],
): NonOverlappingTooltipPosition[] {
  const input = comps.map((op) => {
    return {
      id: op.compId,
      top: op.position.y,
      height: op.height,
    };
  });

  const shiftedPositions = runPositionShift(input, 8, TOOLTIP_STACK_TOP_PADDING);
  const positions = comps.map((op) => {
    const shiftedPosition = shiftedPositions.find((sp) => sp.id === op.compId);
    if (!shiftedPosition) {
      return null;
    }

    // default to 0, to handle the NaN case. We need this to render the component so that resizing logic can occur.
    const boxPositionY = shiftedPosition.newTop || 0;

    return {
      ...op,
      boxPosition: {
        x: op.position.x,
        y: boxPositionY,
      },
    };
  });

  return positions.filter((i) => i !== null);
}

export function CompSummaryTooltip({
  children,
  boxPosition,
  position,
  svgTop,
  svgLeft,
  onHeightChange,
  portalContainer,
}: {
  boxPosition: { x: number; y: number };
  position: { x: number; y: number };
  children: React.ReactNode;
  svgTop: number;
  svgLeft: number;
  onHeightChange: (height: number) => void;
  portalContainer: HTMLElement;
}) {
  const HORIZONTAL_OFFSET = 30;

  return (
    <>
      <line
        x1={position.x}
        y1={position.y}
        x2={boxPosition.x + HORIZONTAL_OFFSET + 1}
        y2={boxPosition.y}
        strokeWidth={1}
        stroke="current"
        className="stroke-foreground"
      />
      <Portal.Root
        container={portalContainer}
        style={{
          position: "absolute",
          top: svgTop,
          left: svgLeft,
          pointerEvents: "none",
          marginLeft: MARGIN.left,
          marginTop: MARGIN.top,
        }}
      >
        <div
          className="absolute"
          style={{
            left: boxPosition.x + HORIZONTAL_OFFSET,
            top: boxPosition.y,
          }}
        >
          <div className="relative" style={{ width: TOOLTIP_WIDTH }}>
            <TooltipBox tooltipWidth={TOOLTIP_WIDTH} onHeightChange={onHeightChange}>
              {children}
            </TooltipBox>
          </div>
        </div>
      </Portal.Root>
    </>
  );
}
